import React from 'react';
import { X, Calendar, Clock, User, Mail, Phone, IndianRupee, CreditCard, CheckCircle } from 'lucide-react';

interface BookingDetails {
  serviceName: string;
  serviceType: 'class' | 'dmit' | 'sound-therapy' | 'lab-test' | 'consultation' | 'wellness';
  date?: string;
  timeSlot?: string;
  dates?: string[]; // For multiple dates
  instructorName?: string;
  amount: number;
  pricingType?: string;
  userDetails: {
    name: string;
    email: string;
    phone: string;
    age?: string;
    gender?: string;
  };
  additionalInfo?: Record<string, any>;
}

interface BookingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bookingDetails: BookingDetails;
  isProcessing?: boolean;
}

const BookingConfirmationModal: React.FC<BookingConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  bookingDetails,
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Confirm Your Booking</h2>
            <p className="text-sm text-slate-500">Please review your booking details</p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            disabled={isProcessing}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {/* Service Details */}
          <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-3">{bookingDetails.serviceName}</h3>
            <div className="space-y-2 text-sm text-slate-600">
              {bookingDetails.instructorName && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Instructor: {bookingDetails.instructorName}</span>
                </div>
              )}
              {bookingDetails.date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Date: {formatDate(bookingDetails.date)}</span>
                </div>
              )}
              {bookingDetails.dates && bookingDetails.dates.length > 0 && (
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 mt-0.5" />
                  <div>
                    <span className="font-medium">Selected Dates:</span>
                    <div className="mt-1 space-y-1">
                      {bookingDetails.dates.map((date, index) => (
                        <div key={index} className="text-xs bg-white px-2 py-1 rounded">
                          {formatDate(date)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {bookingDetails.timeSlot && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Time: {bookingDetails.timeSlot}</span>
                </div>
              )}
              {bookingDetails.pricingType && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  <span>Pricing: {bookingDetails.pricingType}</span>
                </div>
              )}
            </div>
          </div>

          {/* User Details */}
          <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
            <h4 className="text-md font-semibold text-slate-900 mb-3">Your Details</h4>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{bookingDetails.userDetails.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span>{bookingDetails.userDetails.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span>{bookingDetails.userDetails.phone}</span>
              </div>
              {bookingDetails.userDetails.age && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Age:</span>
                  <span>{bookingDetails.userDetails.age}</span>
                </div>
              )}
              {bookingDetails.userDetails.gender && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Gender:</span>
                  <span>{bookingDetails.userDetails.gender}</span>
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-slate-900">Total Amount</span>
                {bookingDetails.dates && bookingDetails.dates.length > 0 && (
                  <span className="text-sm text-emerald-700 mt-1">
                    ({bookingDetails.dates.length} session{bookingDetails.dates.length > 1 ? 's' : ''})
                  </span>
                )}
              </div>
              <div className="flex flex-col items-end">
                <div className="flex items-center text-2xl font-bold text-emerald-600">
                  <IndianRupee className="h-6 w-6 mr-1" />
                  {bookingDetails.amount}
                </div>
                {bookingDetails.dates && bookingDetails.dates.length > 0 && bookingDetails.amount > 0 && (
                  <span className="text-xs text-emerald-600 mt-1 flex items-center">
                    <IndianRupee className="h-3 w-3" />
                    {bookingDetails.amount / bookingDetails.dates.length} / session
                  </span>
                )}
              </div>
            </div>
            {bookingDetails.amount === 0 && (
              <p className="text-sm text-emerald-700 mt-2">This is a free booking</p>
            )}
          </div>

          {/* Additional Info */}
          {bookingDetails.additionalInfo && Object.keys(bookingDetails.additionalInfo).length > 0 && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
              <h4 className="text-md font-semibold text-slate-900 mb-3">Additional Information</h4>
              <div className="space-y-2 text-sm text-slate-600">
                {Object.entries(bookingDetails.additionalInfo).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                    <span>{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isProcessing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Processing...
                </>
              ) : bookingDetails.amount === 0 ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Confirm Booking
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Confirm & Pay
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationModal;